import _merge from "lodash/merge";
import { getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import { getDimensions } from '@kentik/ui-shared/query/query';
import { DEFAULT_QUALITATIVE_PALETTES } from 'app/stores/colors/Colors';
import { formatRowName, getBaseChartStyle } from 'app/views/ude/dataviews/util/highcharts.util';
import { generateTimelineOptions } from 'app/views/ude/dataviews/util/timelineView';
import { dataViewTypes } from 'app/stores/$dataviews';
import { getTimezone, getTimezoneShortName } from 'core/util/timezone';
import { formatMetricValue, getDatasetPrefix, getDnsCodeLabel, getHealthLabel, getHttpStatusCodeLabel, parseUnit } from './unitFormatter';

/**
 * Formatter function for dimension values.
 * Used to convert raw dimension values (like agent IDs) to display names.
 */

/**
 * Y-axis configuration for health metrics
 * Shows only the 3 valid health status labels: Critical (100), Warning (200), Healthy (300)
 */
const HEALTH_YAXIS_CONFIG = {
  tickPositions: [100, 200, 300],
  min: 50,
  max: 350,
  labels: {
    formatter() {
      return getHealthLabel(this.value);
    }
  }
};

/**
 * Y-axis configuration for DNS response code metrics
 * Shows common DNS response codes (0-5) with their labels
 */
const DNS_CODE_YAXIS_CONFIG = {
  tickPositions: [0, 1, 2, 3, 4, 5],
  min: -0.5,
  max: 5.5,
  labels: {
    formatter() {
      return getDnsCodeLabel(this.value);
    }
  }
};

/**
 * Y-axis configuration for HTTP status code metrics
 * Shows major status code categories to avoid crowding
 */
const HTTP_STATUS_CODE_YAXIS_CONFIG = {
  // Show just the major category representatives: 2xx, 3xx, 4xx, 5xx
  tickPositions: [200, 300, 400, 500],
  min: 150,
  max: 550,
  labels: {
    formatter() {
      return getHttpStatusCodeLabel(this.value);
    }
  }
};
const SupportedVizTypes = ['bar', 'line', 'matrix', 'pie', 'stackedArea', 'stackedAreaHundredPercent', 'stackedBar', 'sunburst', 'timelineView'];
export const HighchartsVizTypes = SupportedVizTypes;
export const TimeSeriesVizTypes = ['line', 'stackedBar', 'stackedArea', 'stackedAreaHundredPercent'];
const AggregateSeriesVizTypes = ['bar', 'pie'];
const VizToChartTypes = {
  bar: 'bar',
  line: 'line',
  matrix: 'heatmap',
  pie: 'pie',
  stackedArea: 'area',
  stackedAreaHundredPercent: 'area',
  stackedBar: 'column',
  sunburst: 'sunburst',
  timelineView: 'xrange'
};
function generateTimeSeriesOptions(queryModel, measurement, theme, metricName, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const vizType = queryModel.settings.chart.type;
  const chartType = VizToChartTypes[vizType];
  const timezone = getTimezone(queryModel.settings.chart.xAxis?.timezone || 'UTC');
  const unit = measurementMetric?.Unit;
  const unitFormat = parseUnit(unit);
  const isHealthMetric = unitFormat.type === 'health';
  const isDnsCodeMetric = unitFormat.type === 'dns_code';
  const isHttpStatusCodeMetric = unitFormat.type === 'http_status_code';
  const isMetricsMeasurement = measurement.measurement.startsWith('/metrics');
  const dimensions = getDimensions(queryModel.query);
  let stacking;

  // Get all data for determining consistent prefix/suffix
  const allData = queryModel.results.rows.flatMap(row => row.values[metricName]?.values || []).filter(v => v != null);
  const prefix = getDatasetPrefix(allData, unit);
  if (vizType === 'stackedArea' || vizType === 'stackedBar') {
    stacking = 'normal';
  } else if (vizType === 'stackedAreaHundredPercent') {
    stacking = 'percent';
  }

  // Build Y-axis config - use special config for health, dns_code, and http_status_code metrics
  const yAxisTitle = measurementMetric?.Label || metricName;
  let yAxisConfig;
  if (isHealthMetric) {
    yAxisConfig = {
      ...HEALTH_YAXIS_CONFIG,
      crosshair: false,
      title: {
        text: yAxisTitle
      }
    };
  } else if (isDnsCodeMetric) {
    yAxisConfig = {
      ...DNS_CODE_YAXIS_CONFIG,
      crosshair: false,
      title: {
        text: yAxisTitle
      }
    };
  } else if (isHttpStatusCodeMetric) {
    yAxisConfig = {
      ...HTTP_STATUS_CODE_YAXIS_CONFIG,
      crosshair: false,
      title: {
        text: yAxisTitle
      }
    };
  } else {
    yAxisConfig = {
      crosshair: false,
      type: queryModel.settings.chart?.yAxis?.type,
      title: {
        text: yAxisTitle
      },
      labels: {
        formatter() {
          const formatted = formatMetricValue(this.value, unit, {
            forcePrefix: prefix
          });
          return formatted.displayFull;
        }
      }
    };
  }
  const colors = DEFAULT_QUALITATIVE_PALETTES[queryModel.settings.chart?.colors ?? 'default'];
  const options = {
    chart: {
      type: chartType
    },
    colors,
    legend: {
      enabled: false
    },
    xAxis: {
      type: 'datetime',
      crosshair: true
    },
    yAxis: yAxisConfig,
    time: {
      timezone
    },
    tooltip: {
      shared: true,
      followPointer: true,
      useHTML: true,
      positioner(labelWidth, _labelHeight, point) {
        const OFFSET_X = 20;
        const OFFSET_Y = 24;
        const c = this.chart;
        const plotX = point.plotX + c.plotLeft;
        const plotY = point.plotY + c.plotTop;
        let x = plotX + OFFSET_X + labelWidth > c.chartWidth ? plotX - labelWidth - OFFSET_X : plotX + OFFSET_X;
        x = Math.max(0, Math.min(x, c.chartWidth - labelWidth));
        const y = plotY + OFFSET_Y;
        return {
          x,
          y
        };
      },
      headerFormat: '<strong>{point.key} {point.point.options.custom.timezone}</strong><table>',
      pointFormatter() {
        return `
          <tr>
            <td><span style="color:${this.color}">\u25CF</span> ${this.series.name}</td>
            <td><b>${this.options.custom?.formatted}</b></td>
            ${stacking === 'percent' && this.percentage !== undefined ? `<td>(${this.percentage.toFixed(1)}%)</td>` : ''}
          </tr>`;
      },
      footerFormat: '</table>',
      xDateFormat: '%Y-%m-%d %H:%M'
    },
    plotOptions: {
      series: {
        keys: ['x', 'y', 'custom.formatted', 'custom.timezone'],
        stacking,
        ...(isMetricsMeasurement && {
          connectNulls: true
        })
      }
    },
    series: queryModel.results.rows.filter(row => row.values[metricName]).slice(0, queryModel.effectiveVizLimit).map((row, seriesIdx) => {
      const {
        timestamps
      } = row;
      return {
        name: formatRowName(row.get(), dimensions, dimensionFormatter),
        type: chartType,
        color: colors[seriesIdx % colors.length],
        visible: queryModel.isSeriesVisible(row.id),
        data: row.values[metricName].values.map((value, idx) => [timestamps[idx] * 1000, value, formatMetricValue(isMetricsMeasurement ? value : value ?? 0, unit, {
          values: measurementMetric?.Values
        }).displayFull, getTimezoneShortName(timezone, timestamps[idx] * 1000)])
      };
    })
  };
  const filteredRows = queryModel.results.rows.filter(row => row.values[metricName]).slice(0, queryModel.effectiveVizLimit);
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: filteredRows.map(r => r.id)
  };
}
function generateAggregateSeriesOptions(queryModel, theme, measurement, metricName, rollupName, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const visualizationRows = queryModel.results.rows.slice(0, queryModel.effectiveVizLimit);
  const chartType = VizToChartTypes[queryModel.settings.chart.type];
  const unit = measurementMetric?.Unit;
  const dimensions = getDimensions(queryModel.query);

  // Get all data for determining consistent prefix/suffix
  const allData = queryModel.results.rows.flatMap(row => row.rollups[rollupName]).filter(v => v != null);
  const prefix = getDatasetPrefix(allData, unit);
  const options = {
    chart: {
      type: chartType
    },
    colors: DEFAULT_QUALITATIVE_PALETTES[queryModel.settings.chart?.colors ?? 'default'],
    legend: {
      enabled: false
    },
    xAxis: {
      type: 'category'
    },
    yAxis: {
      type: queryModel.settings.chart.yAxis?.type,
      title: {
        text: measurementMetric?.Label || metricName
      },
      labels: {
        formatter() {
          const formatted = formatMetricValue(this.value, unit, {
            forcePrefix: prefix
          });
          return formatted.displayFull;
        }
      }
    },
    tooltip: {
      shared: true,
      followPointer: true,
      useHTML: true,
      valueDecimals: 1,
      headerFormat: '<div>{point.key}</div><table>',
      pointFormatter() {
        return `
          <tr>
            <td><span style="color:${this.color}">\u25CF</span></td>
            <td><b>${this.options.custom?.formatted}</b></td>
            ${this.percentage !== undefined ? `<td>(${this.percentage.toFixed(1)}%)</td>` : ''}
          </tr>`;
      },
      footerFormat: '</table>'
    },
    series: [{
      name: measurementMetric?.Label || metricName,
      type: chartType,
      data: visualizationRows.map(row => {
        const value = row.rollups[rollupName];
        return {
          name: formatRowName(row.get(), dimensions, dimensionFormatter),
          y: value,
          custom: {
            formatted: formatMetricValue(value, unit).displayFull
          }
        };
      })
    }]
  };
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: visualizationRows.map(r => r.id)
  };
}
function generateSunburstOptions(queryModel, theme, metricName, rollupName, measurement, dimensionFormatter) {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const limit = queryModel.effectiveVizLimit;
  const visualizationRows = queryModel.results.rows.slice(0, limit);
  const unit = measurementMetric?.Unit;
  const colors = DEFAULT_QUALITATIVE_PALETTES[queryModel.settings.chart?.colors ?? 'default'];
  const dimensions = getDimensions(queryModel.query);
  const data = [{
    id: 'root',
    parent: '',
    name: `Total of Top ${limit}`,
    color: 'transparent'
  }];
  let colorIndex = 0;
  visualizationRows.forEach(row => {
    const path = dimensions.flatMap(dimension => {
      const rawName = row.dimensions[dimension.name];
      const formattedName = dimensionFormatter ? dimensionFormatter(dimension.name, rawName) : rawName;
      return dimension.name.includes('bgp_aspath') ? formattedName.split(' ') : formattedName;
    });
    let parent = 'root';
    path.forEach((name, idx) => {
      const id = `${parent}-${name}`;
      let node = data.find(n => n.id === id);
      if (!node) {
        node = {
          id,
          parent,
          name
        };
        if (parent === 'root') {
          node.color = colors[colorIndex % colors.length];
          colorIndex += 1;
        }
        data.push(node);
      }
      if (idx === path.length - 1) {
        node.value ??= 0;
        node.value += row.rollups[rollupName];
      }
      parent = id;
    });
  });
  const options = {
    chart: {
      type: 'sunburst'
    },
    colors: DEFAULT_QUALITATIVE_PALETTES[queryModel.settings.chart?.colors ?? 'default'],
    legend: {
      enabled: false
    },
    tooltip: {
      shared: false,
      followPointer: true,
      useHTML: true,
      valueDecimals: 1,
      headerFormat: '<div>{point.key}</div><table>',
      pointFormatter() {
        const value = Number(this.value) || 0;
        const formatted = formatMetricValue(value, unit).displayFull;
        return `
          <tr>
            <td><span style="color:${this.color}">\u25CF</span></td>
            <td><b>${formatted}</b></td>
          </tr>`;
      },
      footerFormat: '</table>'
    },
    plotOptions: {
      sunburst: {
        dataLabels: {
          rotationMode: 'circular',
          style: {
            textOutline: 'none'
          },
          filter: {
            property: 'outerArcLength',
            operator: '>',
            value: 64
          }
        }
      }
    },
    series: [{
      name: measurementMetric?.Label || metricName,
      type: 'sunburst',
      cursor: 'pointer',
      allowTraversingTree: true,
      levels: [{
        level: 1,
        dataLabels: {
          enabled: false
        }
      }, {
        level: 2,
        colorByPoint: true
      }, {
        level: 3,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 4,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 5,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 6,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 7,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }, {
        level: 8,
        colorVariation: {
          key: 'brightness',
          to: 0.5
        }
      }, {
        level: 9,
        colorVariation: {
          key: 'brightness',
          to: -0.5
        }
      }],
      data
    }]
  };
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: visualizationRows.map(r => r.id)
  };
}

/**
 * Apply threshold configuration to Highcharts options.
 * Adds plotLines and/or plotBands to the yAxis based on threshold settings.
 *
 * For percentage mode, threshold values represent percentages of the data range (0-100).
 * For absolute mode, threshold values are used as-is.
 */
function applyThresholds(options, thresholds, queryModel) {
  const {
    steps,
    mode,
    display,
    colorSeries
  } = thresholds;
  if (display === 'off' || !steps || steps.length < 2) {
    return;
  }

  // Sort steps ascending by value (base step with null goes first)
  const sortedSteps = [...steps].sort((a, b) => {
    if (a.value === null) {
      return -1;
    }
    if (b.value === null) {
      return 1;
    }
    return a.value - b.value;
  });

  // For percentage mode, compute data range min/max from results
  let dataMin = 0;
  let dataMax = 100;
  if (mode === 'percentage') {
    const allValues = queryModel.results.rows.flatMap(row => Object.values(row.values).flatMap(metric => metric?.values?.filter(v => v != null) || []));
    if (allValues.length > 0) {
      dataMin = Math.min(...allValues);
      dataMax = Math.max(...allValues);
    }
  }
  const resolveValue = step => {
    if (step.value === null) {
      return dataMin;
    }
    if (mode === 'percentage') {
      return dataMin + step.value / 100 * (dataMax - dataMin);
    }
    return step.value;
  };
  const showLines = display === 'line' || display === 'dashed-line' || display === 'line+area' || display === 'dashed-line+area';
  const showAreas = display === 'area' || display === 'line+area' || display === 'dashed-line+area';
  const dashed = display === 'dashed-line' || display === 'dashed-line+area';
  const plotLines = [];
  const plotBands = [];

  // Non-base steps get plotLines
  if (showLines) {
    for (const step of sortedSteps) {
      if (step.value !== null) {
        plotLines.push({
          value: resolveValue(step),
          color: step.color,
          width: 2,
          dashStyle: dashed ? 'Dash' : 'Solid',
          zIndex: 5,
          label: {
            text: `${step.value}${mode === 'percentage' ? '%' : ''}`,
            style: {
              color: step.color,
              fontSize: '11px'
            }
          }
        });
      }
    }
  }

  // PlotBands: fill regions between steps
  if (showAreas) {
    for (let i = 0; i < sortedSteps.length; i++) {
      const step = sortedSteps[i];
      const from = resolveValue(step);
      const to = i < sortedSteps.length - 1 ? resolveValue(sortedSteps[i + 1]) : Number.MAX_VALUE;
      plotBands.push({
        from,
        to,
        color: `${step.color}30`,
        // ~19% opacity hex suffix
        zIndex: 0
      });
    }
  }

  // Always explicitly set plotLines and plotBands (even if empty) so that
  // chart.update() clears stale decorations when switching display modes.
  const yAxis = options.yAxis;
  if (yAxis) {
    yAxis.plotLines = plotLines;
    yAxis.plotBands = plotBands;
  }

  // Apply or clear color zones on each series.
  if (options.series) {
    if (colorSeries && showAreas) {
      const zones = sortedSteps.map((step, i) => {
        const zone = {
          color: step.color,
          fillColor: `${step.color}40`
        };
        if (i < sortedSteps.length - 1) {
          zone.value = resolveValue(sortedSteps[i + 1]);
        }
        return zone;
      });
      for (const series of options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = 'y';
        s.zones = zones;
      }
    } else {
      // Clear zones so series revert to original colors
      for (const series of options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = undefined;
        s.zones = [];
      }
    }
  }
}
export function generateHighchartsOptions(queryModel, measurement, theme, dimensionFormatter) {
  const vizType = queryModel.settings.chart.type;
  const metricName = queryModel.primaryMetric;
  const dataView = dataViewTypes?.[vizType || 'line'];
  const requirements = dataView?.requirements?.(queryModel.query);
  if (requirements) {
    console.info(`User attempted an incompatible query: ${queryModel.query} for ${vizType}`);
    const requirementsText = requirements.description ? `${requirements.title} ${requirements.description}` : requirements.title;
    return {
      options: {
        title: {
          text: requirementsText
        }
      },
      seriesRowIds: []
    };
  }
  let result;
  if (vizType && TimeSeriesVizTypes.includes(vizType)) {
    result = generateTimeSeriesOptions(queryModel, measurement, theme, metricName, dimensionFormatter);
  } else if (vizType && AggregateSeriesVizTypes.includes(vizType)) {
    const rollupKey = queryModel.primaryRollupKey;
    result = generateAggregateSeriesOptions(queryModel, theme, measurement, metricName, rollupKey, dimensionFormatter);
  } else if (vizType === 'sunburst') {
    const rollupKey = queryModel.primaryRollupKey;
    result = generateSunburstOptions(queryModel, theme, metricName, rollupKey, measurement, dimensionFormatter);
  } else if (vizType === 'timelineView') {
    result = generateTimelineOptions({
      queryModel,
      theme,
      measurement,
      metricName,
      dimensionFormatter
    });
  } else {
    // TODO matrix
    console.error(`Unsupported VizType: ${vizType}`);
    return {
      options: {
        title: {
          text: 'Coming Soon!'
        }
      },
      seriesRowIds: []
    };
  }

  // Apply thresholds to all chart types that have a yAxis
  const {
    thresholds
  } = queryModel.settings.chart;
  if (thresholds?.enabled && thresholds.display !== 'off') {
    applyThresholds(result.options, thresholds, queryModel);
  } else {
    // Explicitly clear threshold decorations so chart.update() removes stale ones
    const yAxis = result.options.yAxis;
    if (yAxis) {
      yAxis.plotLines = [];
      yAxis.plotBands = [];
    }
    if (result.options.series) {
      for (const series of result.options.series) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const s = series;
        s.zoneAxis = undefined;
        s.zones = [];
      }
    }
  }
  return result;
}